import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import {
  MagnifyingGlassIcon,
  CheckIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';

import type { SerialRow, SerialStatus } from '../adapter/MasterDocumentAdapter.types';
import { ConfirmDialog } from '../DocumentModalList';
import { useSerialPanelTranslation } from './i18n';

/**
 * Mapa de estado de serial -> clave i18n y clases de color del badge.
 * El fallback (estado no mapeado) es `Available` para no regresionar.
 */
const STATUS_BADGE: Record<SerialStatus, { i18nKey: string; className: string }> = {
  Created: {
    i18nKey: 'serial_panel.status_created',
    className: 'bg-slate-50 text-slate-600 dark:bg-slate-900 dark:text-slate-400',
  },
  Available: {
    i18nKey: 'serial_panel.status_available',
    className: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300',
  },
  Active: {
    i18nKey: 'serial_panel.status_active',
    className: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  },
  Committed: {
    i18nKey: 'serial_panel.status_committed',
    className: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  },
  Unavailable: {
    i18nKey: 'serial_panel.status_unavailable',
    className: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  },
};

/** Clave i18n del mensaje de confirmación cuando el consumidor no declara otra. */
const DEFAULT_CONFIRM_MESSAGE_KEY = 'serial_panel.confirm_selection';

export interface SerialSelectPanelProps {
  /**
   * Seriales disponibles para seleccionar.
   *
   * - Modo cliente (default): se provee el conjunto completo y el panel filtra y
   *   pagina en cliente (suficiente para demo y datasets chicos / stories).
   * - Modo servidor (cuando se provee `onQueryChange` u `onPageChange`): `rows`
   *   es ya la PÁGINA actual devuelta por `adapter.fetchAvailableSerials`; el
   *   panel no filtra ni rebana, solo renderiza y delega búsqueda/paginación.
   */
  rows: SerialRow[];
  /** IDs de seriales seleccionados (controlado). */
  value: string[];
  /** Cantidad requerida (M). */
  required: number;
  /** Modo solo lectura (documento fuera de DRAFT). */
  readOnly?: boolean;
  /** Tamaño de página. Default 8. */
  pageSize?: number;
  onChange: (next: string[]) => void;
  className?: string;

  // ── Modo servidor (opcional) ───────────────────────────────
  // Si se provee `onQueryChange` u `onPageChange`, el panel entra en modo
  // controlado/servidor: usa estos valores en lugar de su estado interno y no
  // hace filtrado ni rebanado en cliente.
  /** Texto de búsqueda controlado (modo servidor). */
  query?: string;
  /** Notifica el cambio de búsqueda (activa el modo servidor). */
  onQueryChange?: (query: string) => void;
  /** Página actual (1-based) controlada (modo servidor). */
  page?: number;
  /** Notifica el cambio de página (activa el modo servidor). */
  onPageChange?: (page: number) => void;
  /** Total de registros del lado servidor, para calcular la paginación. */
  total?: number;
  /** Indicador de carga (modo servidor): se muestra mientras llega la página. */
  loading?: boolean;

  // ── Confirmación al marcar (opcional) ──────────────────────
  // Las inyecta `useSerialPanel` desde `serialSupport` del template.
  /**
   * Estados que exigen confirmación Sí/No al MARCAR una fila. Ausente o vacío ⇒
   * comportamiento idéntico al actual (nunca se confirma). DESmarcar nunca
   * confirma, y el tope de sobre-selección se evalúa antes que la confirmación.
   */
  confirmOnStatuses?: SerialStatus[];
  /**
   * Clave i18n del mensaje de la confirmación. Recibe `{serialCode}`.
   * Default: `serial_panel.confirm_selection`.
   */
  confirmMessageKey?: string;
}

function counterClasses(n: number, required: number): string {
  if (n > required)
    return 'bg-red-50 text-red-800 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-800';
  if (n === required)
    return 'bg-green-50 text-green-800 border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-800';
  return 'bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800';
}

/**
 * Panel de SELECCIÓN de seriales (documentos de salida, `NatureStock = Subtract`).
 *
 * Tabla multi-select sobre seriales en estado `Available`, con búsqueda y
 * paginación. Bloquea la over-selección (no permite superar `required`) y muestra
 * un toast. Componente presentacional/controlado: la selección vive en el
 * contenedor; búsqueda y paginación son estado local de presentación.
 */
export function SerialSelectPanel({
  rows,
  value,
  required,
  readOnly = false,
  pageSize = 8,
  onChange,
  className,
  query: queryProp,
  onQueryChange,
  page: pageProp,
  onPageChange,
  total,
  loading = false,
  confirmOnStatuses,
  confirmMessageKey,
}: SerialSelectPanelProps) {
  const { t } = useSerialPanelTranslation();
  // Modo servidor: la búsqueda/paginación se delega al contenedor.
  const serverMode = onQueryChange !== undefined || onPageChange !== undefined;

  const [queryLocal, setQueryLocal] = useState('');
  const [pageLocal, setPageLocal] = useState(1);
  const [overAttempted, setOverAttempted] = useState(false);
  /**
   * Fila cuya selección espera confirmación. `null` ⇒ sin diálogo abierto.
   * Se guarda la fila entera, no el id: el mensaje necesita el `serialCode` y
   * `rows` puede haber cambiado (refetch del contenedor) para cuando se responda.
   */
  const [pendingRow, setPendingRow] = useState<SerialRow | null>(null);

  const query = serverMode ? queryProp ?? '' : queryLocal;
  const setQuery = (q: string) => {
    if (serverMode) onQueryChange?.(q);
    else setQueryLocal(q);
  };

  const selected = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    // En modo servidor `rows` ya viene filtrado/paginado por el adapter.
    if (serverMode) return rows;
    const q = query.trim().toLowerCase();
    return q ? rows.filter((r) => r.serialCode.toLowerCase().includes(q)) : rows;
  }, [rows, query, serverMode]);

  // Total real: en servidor lo provee el contenedor; en cliente es el set filtrado.
  const totalCount = serverMode ? total ?? rows.length : filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const rawPage = serverMode ? pageProp ?? 1 : pageLocal;
  const currentPage = Math.min(rawPage, totalPages);
  const start = (currentPage - 1) * pageSize;
  // En modo servidor `rows` ya es la página → no rebanar.
  const pageItems = serverMode ? filtered : filtered.slice(start, start + pageSize);
  const goToPage = (p: number) => {
    const clamped = Math.max(1, Math.min(totalPages, p));
    if (serverMode) onPageChange?.(clamped);
    else setPageLocal(clamped);
  };

  const n = value.length;

  /** Agrega el serial a la selección. Única ruta de alta, con o sin confirmación. */
  const commitSelection = (id: string) => {
    setOverAttempted(false);
    onChange([...value, id]);
  };

  /**
   * Fila que exige confirmación al marcarse, o `null`. Una lista ausente o vacía,
   * un estado fuera de ella o un id que no está en `rows` ⇒ `null`, y entonces el
   * camino es bit a bit el de antes de existir `confirmOnStatuses`.
   */
  const rowToConfirm = (id: string): SerialRow | null => {
    if (!confirmOnStatuses || confirmOnStatuses.length === 0) return null;
    const row = rows.find((r) => r.serialId === id);
    return row && confirmOnStatuses.includes(row.status) ? row : null;
  };

  const toggle = (id: string) => {
    if (readOnly) return;
    if (selected.has(id)) {
      setOverAttempted(false);
      onChange(value.filter((x) => x !== id));
      return;
    }
    if (n >= required) {
      // ANTES de confirmar a propósito: no se pregunta por un serial que de
      // todos modos sería rechazado por exceder la cantidad requerida.
      setOverAttempted(true);
      return;
    }
    const rowNeedingConfirm = rowToConfirm(id);
    if (rowNeedingConfirm) {
      setPendingRow(rowNeedingConfirm);
      return;
    }
    commitSelection(id);
  };

  /**
   * Respuesta afirmativa del diálogo. Re-evalúa los guards del `toggle`: entre
   * abrir el diálogo y responder, el contenedor pudo cambiar `readOnly`, `value`
   * o `required` (una regla de servidor sobre la cantidad de la línea, p.ej.),
   * y la confirmación no puede saltarse el tope ni escribir en solo lectura.
   *
   * El serial también pudo dejar de estar en `rows`: si el ítem de la línea
   * cambió, `useSerialPanel` limpia los seriales y recarga el inventario, y
   * agregar el pendiente escribiría un id del inventario anterior.
   */
  const confirmPending = () => {
    if (pendingRow === null) return;
    const id = pendingRow.serialId;
    setPendingRow(null);
    if (readOnly || selected.has(id)) return;
    if (!rows.some((r) => r.serialId === id)) return;
    if (n >= required) {
      setOverAttempted(true);
      return;
    }
    commitSelection(id);
  };

  const isEmpty = filtered.length === 0;

  return (
    <div className={clsx('flex flex-col gap-4', className)}>
      {readOnly && (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
          <LockClosedIcon className="h-3.5 w-3.5" />
          {t('serial_panel.readonly_banner')}
        </div>
      )}

      {/* Header: búsqueda + contador */}
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            disabled={readOnly}
            placeholder={t('serial_panel.search_placeholder')}
            onChange={(e) => {
              setQuery(e.target.value);
              goToPage(1);
            }}
            className="h-9 w-full rounded-lg border-[1.5px] border-slate-300 pl-9 pr-3 text-[13px] outline-none transition focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/15 disabled:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:disabled:bg-slate-800"
          />
        </div>
        <span
          className={clsx(
            'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[13px] font-semibold',
            counterClasses(n, required),
          )}
        >
          <CheckIcon className="h-3 w-3 stroke-[2.5]" />
          {t('serial_panel.select_counter_label', { selected: n, required })}
        </span>
      </div>

      {/* Toast over-selección */}
      {overAttempted && (
        <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3.5 py-2.5 text-[13px] text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <ExclamationCircleIcon className="h-[15px] w-[15px] shrink-0" />
          {/* `required` viaja ADEMÁS de `count`: el factory interpola solo las variables que
              recibe, y un catálogo de host que declare el texto con `{required}` (los hubo, y
              los hay en ambientes cuyo /langs va por detrás) dejaba el token crudo en pantalla.
              Pasar las dos hace el mensaje correcto con CUALQUIER catálogo. */}
          <span>{t('serial_panel.overcount_error', { count: required, required })}</span>
        </div>
      )}

      {/* Tabla / estado vacío */}
      <div className="relative overflow-hidden rounded-lg border-[1.5px] border-slate-200 dark:border-slate-700">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-slate-900/60">
            <svg className="h-5 w-5 animate-spin text-primary-600" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
              <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          </div>
        )}
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center gap-2.5 px-5 py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
              <ArchiveBoxIcon className="h-6 w-6" />
            </div>
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {t('serial_panel.empty_title')}
            </div>
            <div className="text-[13px] text-slate-500 dark:text-slate-400">
              {t('serial_panel.empty_state')}
            </div>
          </div>
        ) : (
          <>
            <table className="w-full border-collapse text-[13px]">
              <thead className="border-b-[1.5px] border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
                <tr>
                  <th className="w-[42px] px-3.5 py-2.5" />
                  <th className="px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('serial_panel.col_serial')}
                  </th>
                  <th className="px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('serial_panel.col_lot')}
                  </th>
                  <th className="px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('serial_panel.col_location')}
                  </th>
                  <th className="px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t('serial_panel.col_status')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((row) => {
                  const isSel = selected.has(row.serialId);
                  const badge = STATUS_BADGE[row.status] ?? STATUS_BADGE.Available;
                  return (
                    <tr
                      key={row.serialId}
                      onClick={() => toggle(row.serialId)}
                      className={clsx(
                        'border-b border-slate-100 transition last:border-b-0 dark:border-slate-800',
                        !readOnly && 'cursor-pointer',
                        isSel
                          ? 'bg-primary-50 hover:bg-primary-100 dark:bg-primary-950/40 dark:hover:bg-primary-950/60'
                          : 'hover:bg-slate-50 dark:hover:bg-slate-800/50',
                      )}
                    >
                      <td className="px-3.5 py-2.5 text-center">
                        <span
                          className={clsx(
                            'inline-flex h-[17px] w-[17px] items-center justify-center rounded border-2 transition',
                            isSel
                              ? 'border-primary-600 bg-primary-600'
                              : 'border-slate-400 bg-white dark:bg-slate-900',
                          )}
                        >
                          {isSel && <CheckIcon className="h-2.5 w-2.5 stroke-[3] text-white" />}
                        </span>
                      </td>
                      <td className="px-3.5 py-2.5">
                        <span className="font-mono font-semibold text-slate-800 dark:text-slate-100">
                          {row.serialCode}
                        </span>
                      </td>
                      <td className="px-3.5 py-2.5 text-slate-600 dark:text-slate-300">
                        {row.lotCode ?? '—'}
                      </td>
                      <td className="px-3.5 py-2.5 text-slate-600 dark:text-slate-300">
                        {row.locationCode ?? '—'}
                      </td>
                      <td className="px-3.5 py-2.5">
                        <span
                          className={clsx(
                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                            badge.className,
                          )}
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          {t(badge.i18nKey)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalCount > pageSize && (
              <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-3.5 py-2.5 dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {t('serial_panel.pagination', {
                    from: totalCount === 0 ? 0 : start + 1,
                    to: Math.min(start + pageSize, totalCount),
                    total: totalCount,
                  })}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => goToPage(currentPage - 1)}
                    className="flex h-[30px] w-[30px] items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    <ChevronLeftIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                    className="flex h-[30px] w-[30px] items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    <ChevronRightIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Resumen de seleccionados */}
      {n > 0 && (
        <div className="rounded-lg border-[1.5px] border-primary-200 bg-primary-50 px-3.5 py-3 dark:border-primary-900 dark:bg-primary-950/30">
          <div className="mb-2 text-xs font-semibold text-primary-700 dark:text-primary-300">
            {t('serial_panel.selected_section')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {value.map((id) => {
              const row = rows.find((r) => r.serialId === id);
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded-full border border-primary-300 bg-primary-100 px-2 py-0.5 font-mono text-[11px] font-semibold text-primary-700 dark:border-primary-800 dark:bg-primary-950 dark:text-primary-300"
                >
                  {row ? row.serialCode : id}
                  {!readOnly && (
                    <button
                      type="button"
                      aria-label={t('serial_panel.remove_aria')}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle(id);
                      }}
                      className="rounded-full opacity-70 transition hover:opacity-100"
                    >
                      <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/*
        Confirmación al marcar. Va en un portal a `document.body` porque el
        `ConfirmDialog` es `position: fixed` y el modal de línea que contiene al
        panel lleva `-translate-x-1/2` (una transform crea bloque contenedor para
        los descendientes fijos) más `overflow-hidden`: sin portal, el diálogo
        quedaría anclado y recortado dentro de la tarjeta del modal.
        Sin `confirmOnStatuses` `pendingRow` nunca sale de `null` y aquí no se
        renderiza nada: el DOM queda idéntico al de antes del cambio.
      */}
      {pendingRow !== null &&
        createPortal(
          <ConfirmDialog
            message={t(confirmMessageKey ?? DEFAULT_CONFIRM_MESSAGE_KEY, {
              serialCode: pendingRow.serialCode,
            })}
            onConfirm={confirmPending}
            onCancel={() => setPendingRow(null)}
          />,
          document.body,
        )}
    </div>
  );
}
